import { Component, OnInit, ChangeDetectorRef } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, FormsModule } from '@angular/forms';
import { Router, ActivatedRoute } from '@angular/router';
import { ButtonModule } from 'primeng/button';
import { AccordionModule } from 'primeng/accordion';
import { TableModule } from 'primeng/table';
import { SelectModule } from 'primeng/select';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { DatePickerModule } from 'primeng/datepicker';
import { InputTextModule } from 'primeng/inputtext';
import { InputNumberModule } from 'primeng/inputnumber';
import { TagModule } from 'primeng/tag';
import { TooltipModule } from 'primeng/tooltip';
import { DialogModule } from 'primeng/dialog';
import { CheckboxModule } from 'primeng/checkbox';
import { ToasterService } from '@ui-nuvertos/auth';
import { lastValueFrom } from 'rxjs';
import { DynamicForm } from '../dynamic-form/dynamic-form';
import {
  AssetFulfillmentService,
  FulfillmentItem,
} from '../services/asset-fulfillment/asset-fulfillment.service';
import { AssetRequestService } from '../services/asset-request/asset-request.service';
import { SubsidiaryService } from '../services/subsidiary/subsidiary.service';
import { UserService } from '../services/user/user.service';
import { MastersService } from '../services/masters/masters.service';
import { ItemService } from '../services/item/item.service';
import { AssetTransferService } from '../services/asset-transfer/asset-transfer.service';
import { AssetService } from '../services/asset/asset.service';
import { AssetHistoryModalComponent } from '../asset-create/asset-history-modal.component';
import { STANDALONE_MODE } from '../utils/standalone.utils';

interface AvailableAssetRow {
  asset: any | null;
  assignToDepartment: any | null;
  assignToPerson: any | null;
  expanded: boolean;
  assetSuggestions: any[];
  userSuggestions: any[];
  departmentSuggestions: any[];
}

interface NotAvailableAssetRow {
  fulfillmentType: string;
  asset: any | null;
  quantity: number;
  expanded: boolean;
  assetSuggestions: any[];
  // Manual dropdowns (subsidiary/location with dependent logic)
  transferTargetSubsidiary: any | null;
  transferTargetLocation: any | null;
  targetLocationOptions: any[];
  // Dynamic form for transfer details
  transferForm: FormGroup;
  // Shared asset info fields (used by both transfer and procurement)
  assetGroupName: string;
  assetSubGroupName: string;
  assetLocation: string;
  assetCenter: string;
  // Procurement fields
  assetGroup: any | null;
  assetSubGroup: any | null;
  groupSuggestions: any[];
  subGroupSuggestions: any[];
  itemMaster: any | null;
  itemMasterSuggestions: any[];
}

@Component({
  selector: 'lib-asset-fulfillment',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    AccordionModule,
    TableModule,
    SelectModule,
    AutoCompleteModule,
    DatePickerModule,
    InputTextModule,
    InputNumberModule,
    TagModule,
    TooltipModule,
    DialogModule,
    CheckboxModule,
    DynamicForm,
    AssetHistoryModalComponent,
  ],
  templateUrl: './asset-fulfillment.html',
  styleUrl: './asset-fulfillment.css',
})
export class AssetFulfillmentComponent implements OnInit {
  mode: 'create' | 'view' | 'edit' = 'create';
  requestId = '';
  itemId = '';
  isLoading = false;
  isSubmitting = false;

  // Form for DynamicForm primary info
  form: FormGroup;
  primaryInfoFields: any[] = [];
  primaryInfoExpanded = true;

  // Request + item data
  requestData: any = null;
  itemData: any = null;
  inStockCount = 0;

  // Fulfillment data for view mode
  fulfillments: any[] = [];

  // GRN Assets Modal
  showGrnAssetsModal = false;
  grnAssets: any[] = [];
  selectedGrnAssets: any[] = [];
  grnModalFulfillment: any = null;

  // Assignment rows for procurement fulfillments (expanded sub-rows)
  // key = fulfillment id, value = array of selected assets with assignment fields
  fulfillmentAssignments: Map<string, any[]> = new Map();
  expandedFulfillmentId: string | null = null;
  fulfillmentAssetDetails: Map<string, any> = new Map();

  // Asset History Modal
  historyModalVisible = false;
  selectedAssetIdForHistory: string | null = null;

  // Available assets section (create mode)
  availableAssetRows: AvailableAssetRow[] = [];

  // Not available assets section (create mode)
  notAvailableAssetRows: NotAvailableAssetRow[] = [];

  // Dropdown options
  departmentOptions: any[] = [];
  subsidiaryOptions: any[] = [];
  subsidiariesData: any[] = [];
  fulfillmentTypeOptions = STANDALONE_MODE
    ? [{ label: 'Transfer Asset', value: 'transfer' }]
    : [
        { label: 'Purchase New Asset', value: 'procurement' },
        { label: 'Transfer Asset', value: 'transfer' },
      ];
  transferTypeOptions = [
    { label: 'Permanent', value: 'permanent' },
    { label: 'Temporary', value: 'temporary' },
    { label: 'Loan', value: 'loan' },
  ];
  transferModeOptions = [
    { label: 'Internal', value: 'internal' },
    { label: 'External', value: 'external' },
  ];

  // Transfer template fields
  transferAssetInfoFields: any[] = [];
  transferDetailFields: any[] = [];

  constructor(
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private cdr: ChangeDetectorRef,
    private toasterService: ToasterService,
    private fulfillmentService: AssetFulfillmentService,
    private assetRequestService: AssetRequestService,
    private subsidiaryService: SubsidiaryService,
    private userService: UserService,
    private mastersService: MastersService,
    private itemService: ItemService,
    private assetTransferService: AssetTransferService,
    private assetService: AssetService
  ) {
    this.form = this.fb.group({});
  }

  async ngOnInit() {
    this.requestId = this.route.snapshot.paramMap.get('requestId') || '';
    this.itemId = this.route.snapshot.queryParamMap.get('itemId') || '';
    this.mode =
      (this.route.snapshot.queryParamMap.get('mode') as 'create' | 'view' | 'edit') ||
      'create';

    if (!this.requestId || !this.itemId) {
      this.toasterService.error('Missing request or item ID');
      this.router.navigate(['/assets/asset-request/list']);
      return;
    }

    this.isLoading = true;
    try {
      await this.loadData();
    } catch (error) {
      console.error('Error loading fulfillment data:', error);
      this.toasterService.error('Failed to load fulfillment data');
    } finally {
      this.isLoading = false;
      this.cdr.detectChanges();
    }
  }

  async fetchMasters() {
    try {
      const res = await lastValueFrom(
        this.mastersService.getMasters(['departments'])
      );

      if (res?.data?.departments) {
        this.departmentOptions = res.data?.departments.map((d: any) => ({
          label: d.name,
          value: d.id
        }));
      }
    } catch (err) { 
      console.error('Failed to fetch department masters', err);
      this.departmentOptions = [];
    }
  }

  private async loadData() {
    const [requestRes, subRes] = await Promise.all([
      lastValueFrom(this.assetRequestService.getAssetRequest(this.requestId)),
      lastValueFrom(
        this.subsidiaryService.getSubsidiaries({
          limit: '1000',
          is_active: 'true',
        })
      ),
      this.fetchMasters(),
    ]);

    if (requestRes?.data) {
      this.requestData = requestRes.data;
      if (this.requestData.items) {
        this.itemData = this.requestData.items.find(
          (i: any) => String(i.id) === String(this.itemId)
        );
      }
    }

    if (subRes?.data?.subsidiaries) {
      this.subsidiariesData = subRes.data.subsidiaries;
      this.subsidiaryOptions = this.subsidiariesData.map((s: any) => ({
        label: s.name,
        value: s.id,
      }));
    }

    if (!this.itemData) {
      this.toasterService.error('Item not found in request');
      return;
    }

    // Load available asset count
    await this.loadInStockCount();

    // Load primary info fields from backend (falls back to hardcoded defaults)
    await this.loadPrimaryInfoFields();
    this.patchPrimaryInfoForm();

    // Load transfer template fields (falls back to hardcoded defaults)
    await this.loadTransferTemplateFields();

    if (this.mode === 'view' || this.mode === 'edit') {
      await this.loadFulfillments();
    } else {
      const rowCount = Math.min(this.inStockCount, this.getRemainingQuantity());
      for (let i = 0; i < rowCount; i++) {
        this.addAvailableAssetRow();
      }
    }
  }

  private async loadPrimaryInfoFields() {
    try {
      const res = await lastValueFrom(
        this.fulfillmentService.getFormTemplates()
      );
      if (res?.data?.length) {
        const template = res.data[0];
        // Load full template by ID to get fields
        const templateRes = await lastValueFrom(
          this.fulfillmentService.getFormTemplateById(String(template.id))
        );
        if (templateRes?.data?.fields?.length) {
          this.primaryInfoFields = templateRes.data.fields;
          return;
        }
      } else {
        // No templates exist — fetch default from backend
        const defaultRes = await lastValueFrom(
          this.fulfillmentService.getDefaultFormTemplate()
        );
        if (defaultRes?.data?.fields?.length) {
          this.primaryInfoFields = defaultRes.data.fields;
          return;
        }
      }
    } catch {
    }
  }

  private async loadTransferTemplateFields() {
    try {
      const res = await lastValueFrom(
        this.assetTransferService.getDefaultFormTemplate()
      );
      if (res?.data?.fields?.length) {
        const fields = res.data.fields;
        this.transferAssetInfoFields = fields
          .filter((f: any) => f.sub_tab_id === 1)
          .sort((a: any, b: any) => a.display_order - b.display_order);
        const allDetailFields = fields
          .filter((f: any) => f.sub_tab_id === 2)
          .sort((a: any, b: any) => a.display_order - b.display_order);
        // Exclude subsidiary/location — handled as manual dropdowns
        this.transferDetailFields = allDetailFields.filter(
          (f: any) =>
            f.field_name !== 'destination_subsidiary_id' &&
            f.field_name !== 'destination_location_id'
        );
      }
    } catch {
    }
    this.setTransferFieldOptions();
  }

  private setTransferFieldOptions() {
    this.transferDetailFields.forEach((field: any) => {
      if (field.field_name === 'transfer_type') {
        field.options = this.transferTypeOptions;
      }
      if (field.field_name === 'transfer_mode') {
        field.options = this.transferModeOptions;
      }
      if (field.field_name === 'destination_department') {
        field.options = this.departmentOptions;
      }
    });
  }

  private patchPrimaryInfoForm() {
    const values: Record<string, any> = {
      asset_group_name: this.itemData?.asset_group_name || '-',
      asset_sub_group_name: this.itemData?.asset_sub_group_name || '-',
      asset_name: this.itemData?.asset_name || '-',
      quantity: this.itemData?.quantity || 0,
      fulfilled_quantity: this.itemData?.fulfilled_quantity || 0,
      remaining_quantity: this.getRemainingQuantity(),
      priority: this.itemData?.priority || '-',
      required_by: this.itemData?.required_by || null,
      return_date: this.itemData?.return_date || null,
      remarks: this.itemData?.remarks || '-',
      in_stock_count: this.inStockCount,
    };

    for (const [key, value] of Object.entries(values)) {
      if (!this.form.contains(key)) {
        this.form.addControl(key, this.fb.control(value));
      } else {
        this.form.get(key)?.patchValue(value);
      }
    }
  }

  private async loadInStockCount() {
    try {
      const filters: any = {
        status: 'active',
        limit: 1,
        offset: 0,
      };
      if (this.itemData.asset_group_id) {
        filters.asset_group_id = String(this.itemData.asset_group_id);
      }
      if(this.itemData.asset_sub_group_id){
        filters.asset_sub_group_id = String(this.itemData.asset_sub_group_id);
      }
      if (this.requestData.subsidiary_id) {
        filters.subsidiary_id = String(this.requestData.subsidiary_id);
      }
      if (this.requestData.location_id) {
        filters.location_id = String(this.requestData.location_id);
      }
      const res = await lastValueFrom(
        this.fulfillmentService.getAvailableAssets(filters)
      );
      this.inStockCount = res?.data?.total || 0;
    } catch {
      this.inStockCount = 0;
    }
  }

  private async loadFulfillments() {
    try {
      const res = await lastValueFrom(
        this.fulfillmentService.getFulfillments(this.requestId)
      );
      if (res?.data) {
        const allFulfillments = Array.isArray(res.data) ? res.data : [];
        this.fulfillments = allFulfillments.filter(
          (f: any) => String(f.asset_request_item_id) === String(this.itemId)
        );

        // Load embedded assignments for each fulfillment
        this.fulfillmentAssignments.clear();
        for (const f of this.fulfillments) {
          if (f.assignments && Array.isArray(f.assignments) && f.assignments.length > 0) {
            const rows = f.assignments.map((a: any) => ({
              asset_id: String(a.asset_id),
              asset_name: a.asset_name || '',
              asset_code: a.asset_code || '',
              serial_number: a.serial_number || '',
              model_no: a.model_no || '',
              assignedUser: a.assigned_to_user_id ? { label: a.assigned_to_user_name, value: String(a.assigned_to_user_id) } : null,
              assignedDepartment: a.assigned_to_department_id ? { label: a.assigned_to_department_name, value: String(a.assigned_to_department_id) } : null,
              userSuggestions: [] as any[],
              departmentSuggestions: [...this.departmentOptions],
              isExisting: !!(a.id && String(a.id) !== '0') // Only mark as existing if it has a real saved assignment ID
            }));
            this.fulfillmentAssignments.set(String(f.id), rows);
          }
        }
      }
    } catch (error) {
      console.error('Error loading fulfillments:', error);
    }
  }

  // ============================================
  // Available Assets Section
  // ============================================

  addAvailableAssetRow() {
    this.availableAssetRows.push({
      asset: null,
      assignToDepartment: null,
      assignToPerson: null,
      expanded: false,
      assetSuggestions: [],
      userSuggestions: [],
      departmentSuggestions: [],
    });
  }

  removeAvailableAssetRow(index: number) {
    this.availableAssetRows.splice(index, 1);
  }

  toggleAvailableAssetExpand(index: number) {
    this.availableAssetRows[index].expanded =
      !this.availableAssetRows[index].expanded;
  }

  async searchAvailableAssets(event: any, row: AvailableAssetRow) {
    const query = event.query || '';
    try {
      const filters: any = {
        status: 'active',
        search: query,
        limit: 20,
      };
      if (this.itemData.asset_group_id) {
        filters.asset_group_id = String(this.itemData.asset_group_id);
      }
      if(this.itemData.asset_sub_group_id){
        filters.asset_sub_group_id = String(this.itemData.asset_sub_group_id);
      }
      if (this.requestData.subsidiary_id) {
        filters.subsidiary_id = String(this.requestData.subsidiary_id);
      }
      if (this.requestData.location_id) {
        filters.location_id = String(this.requestData.location_id);
      }
      const res = await lastValueFrom(
        this.fulfillmentService.getAvailableAssets(filters)
      );
      const assets = res?.data?.assets || [];
      // Filter out assets already selected in other rows
      const selectedAssetIds = new Set(
        this.availableAssetRows
          .filter(r => r !== row && r.asset?.id)
          .map(r => String(r.asset.id))
      );
      row.assetSuggestions = assets
        .filter((a: any) => !selectedAssetIds.has(String(a.id)))
        .map((a: any) => ({
          ...a,
          label: `${a.asset_code || ''} - ${a.asset_name || ''}${a.serial_number ? ' (' + a.serial_number + ')' : ''}`,
          _assetId: a.id,
        }));
    } catch {
      row.assetSuggestions = [];
    }
  }

  async searchUsers(event: any, row: AvailableAssetRow) {
    const query = event.query || '';
    try {
      const res = await lastValueFrom(
        this.userService.getAllUsers({
          limit: 50,
          offset: 0,
          is_active: true,
          ...(query ? { name: query } : {}),
        })
      );
      row.userSuggestions = (res.users || []).map((u: any) => ({
        label: u.username,
        value: u.id,
      }));
    } catch {
      row.userSuggestions = [];
    }
  }

  searchAvailableDepartments(event: any, row: AvailableAssetRow) {
    const query = (event.query || '').toLowerCase();
    row.departmentSuggestions = this.departmentOptions.filter(
      (d: any) => d.label.toLowerCase().includes(query)
    );
  }

  // ============================================
  // Not Available Assets Section
  // ============================================

  addNotAvailableAssetRow() {
    const transferForm = this.fb.group({
      // Asset info (read-only labels, populated on asset select)
      asset_group_name: ['-'],
      asset_sub_group_name: ['-'],
      asset_location: ['-'],
      asset_center: ['-'],
      // Transfer details (editable, populated by DynamicForm)
      destination_department: [''],
      transfer_type: ['permanent'],
      transfer_mode: ['internal'],
      requested_date: [null],
      loan_return_date: [null],
      expected_delivery_date: [null],
      delivery_challan_no: [''],
      remarks: [''],
    });

    // Pre-fill target subsidiary and location from asset request
    let prefillSubsidiary: any = null;
    let prefillLocation: any = null;
    let prefillLocationOptions: any[] = [];

    if (this.requestData?.subsidiary_id) {
      prefillSubsidiary = this.subsidiaryOptions.find(
        (s: any) => String(s.value) === String(this.requestData.subsidiary_id)
      ) || null;

      if (prefillSubsidiary) {
        const subsidiary = this.subsidiariesData.find(
          (s: any) => String(s.id) === String(this.requestData.subsidiary_id)
        );
        if (subsidiary?.tax_information && Array.isArray(subsidiary.tax_information)) {
          prefillLocationOptions = subsidiary.tax_information
            .filter((t: any) => !t.is_deleted)
            .map((t: any) => ({ label: t.address || `Location ${t.id}`, value: t.id }));
        }

        if (this.requestData.location_id && String(this.requestData.location_id) !== '0') {
          prefillLocation = prefillLocationOptions.find(
            (l: any) => String(l.value) === String(this.requestData.location_id)
          ) || null;
        }
      }
    }

    this.notAvailableAssetRows.push({
      fulfillmentType: 'procurement',
      asset: null,
      quantity: 1,
      expanded: false,
      assetSuggestions: [],
      transferTargetSubsidiary: prefillSubsidiary,
      transferTargetLocation: prefillLocation,
      targetLocationOptions: prefillLocationOptions,
      transferForm,
      assetGroupName: '',
      assetSubGroupName: '',
      assetLocation: '',
      assetCenter: '',
      assetGroup: null,
      assetSubGroup: null,
      groupSuggestions: [],
      subGroupSuggestions: [],
      itemMaster: null,
      itemMasterSuggestions: [],
    });
  }

  removeNotAvailableAssetRow(index: number) {
    this.notAvailableAssetRows.splice(index, 1);
  }

  toggleNotAvailableAssetExpand(index: number) {
    this.notAvailableAssetRows[index].expanded =
      !this.notAvailableAssetRows[index].expanded;
  }

  async searchTransferAssets(event: any, row: NotAvailableAssetRow) {
    const query = event.query || '';
    try {
      const filters: any = {
        status: 'active',
        search: query,
        limit: 20,
      };
      if (this.itemData.asset_group_id) {
        filters.asset_group_id = String(this.itemData.asset_group_id);
      }
      if (this.requestData.subsidiary_id) {
        filters.exclude_subsidiary_id = String(this.requestData.subsidiary_id);
      }
      if (this.requestData.location_id) {
        filters.exclude_location_id = String(this.requestData.location_id);
      }
      const res = await lastValueFrom(
        this.fulfillmentService.getAvailableAssets(filters)
      );
      const assets = res?.data?.assets || [];
      row.assetSuggestions = assets.map((a: any) => ({
        ...a,
        label: `${a.asset_code || ''} - ${a.asset_name || ''}${a.serial_number ? ' (' + a.serial_number + ')' : ''}`,
        _assetId: a.id,
      }));
    } catch {
      row.assetSuggestions = [];
    }
  }

  onTransferAssetSelected(row: NotAvailableAssetRow) {
    if (row.asset) {
      row.assetGroupName = row.asset.asset_group_name || '-';
      row.assetSubGroupName = row.asset.asset_sub_group_name || '-';
      row.assetLocation = row.asset.location_name || '-';
      row.assetCenter = row.asset.subsidiary_name || row.asset.center_name || '-';
      // Patch into transferForm for DynamicForm display
      row.transferForm.patchValue({
        asset_group_name: row.assetGroupName,
        asset_sub_group_name: row.assetSubGroupName,
        asset_location: row.assetLocation,
        asset_center: row.assetCenter,
      });
    }
  }

  onTransferSubsidiaryChanged(row: NotAvailableAssetRow) {
    if (!row.transferTargetSubsidiary) {
      row.targetLocationOptions = [];
      row.transferTargetLocation = null;
      return;
    }
    const subsidiary = this.subsidiariesData.find(
      (s: any) =>
        String(s.id) === String(row.transferTargetSubsidiary?.value || row.transferTargetSubsidiary)
    );
    if (subsidiary?.tax_information && Array.isArray(subsidiary.tax_information)) {
      row.targetLocationOptions = subsidiary.tax_information
        .filter((t: any) => !t.is_deleted)
        .map((t: any) => ({
          label: t.address || `Location ${t.id}`,
          value: t.id,
        }));
    } else {
      row.targetLocationOptions = [];
    }
    row.transferTargetLocation = null;
  }

  onFulfillmentTypeChanged(row: NotAvailableAssetRow) {
    if (row.fulfillmentType !== 'transfer') {
      row.asset = null;
      row.transferTargetSubsidiary = null;
      row.transferTargetLocation = null;
      row.expanded = false;
    }
    if (row.fulfillmentType !== 'procurement') {
      row.assetGroup = null;
      row.assetSubGroup = null;
      row.itemMaster = null;
      row.itemMasterSuggestions = [];
    }
  }

  async searchProcurementGroups(event: any, row: NotAvailableAssetRow) {
    const query = event.query || '';
    try {
      const res = await lastValueFrom(
        this.mastersService.getAssetGroups({ search: query, limit: 20 })
      );
      row.groupSuggestions = (res?.data?.asset_groups || res?.data || []);
    } catch {
      row.groupSuggestions = [];
    }
  }

  onProcurementGroupSelected(row: NotAvailableAssetRow) {
    row.assetSubGroup = null;
    row.subGroupSuggestions = [];
    if (row.assetGroup) {
      row.assetGroupName = row.assetGroup.name || '';
    }
  }

  async searchProcurementSubGroups(event: any, row: NotAvailableAssetRow) {
    const query = event.query || '';
    try {
      const params: any = { search: query, limit: 20 };
      if (row.assetGroup?.id) {
        params.asset_group_id = String(row.assetGroup.id);
      }
      const res = await lastValueFrom(
        this.mastersService.getAssetSubGroups(params)
      );
      row.subGroupSuggestions = (res?.data?.asset_sub_groups || res?.data || []);
    } catch {
      row.subGroupSuggestions = [];
    }
  }

  onProcurementSubGroupSelected(row: NotAvailableAssetRow) {
    if (row.assetSubGroup) {
      row.assetSubGroupName = row.assetSubGroup.name || '';
    }
  }

  async searchProcurementItems(event: any, row: NotAvailableAssetRow) {
    const query = event.query || '';
    try {
      const res = await lastValueFrom(
        this.itemService.searchItems({
          name: query,
          category_id: 1,
          limit: 20,
          item_type: 2,
          ...(this.itemData?.asset_group_id ? { asset_group_id: String(this.itemData.asset_group_id) } : {}),
          ...(this.itemData?.asset_sub_group_id ? { asset_sub_group_id: String(this.itemData.asset_sub_group_id) } : {}),
        })
      );
      row.itemMasterSuggestions = (res?.data?.items || []).map((item: any) => ({
        ...item,
        label: item.item_master_name || item.name,
      }));
    } catch {
      row.itemMasterSuggestions = [];
    }
  }

  onProcurementItemSelected(row: NotAvailableAssetRow) {
    if (row.itemMaster) {
      row.assetGroupName = row.itemMaster.asset_group_name || '';
      row.assetSubGroupName = row.itemMaster.asset_sub_group_name || '';
    }
  }

  // ============================================
  // Form Submission
  // ============================================

  private extractId(obj: any): string | null {
    if (obj == null) return null;
    // If it's a primitive (string/number), use it directly
    if (typeof obj === 'string' || typeof obj === 'number') {
      const val = String(obj);
      return val && val !== 'undefined' && val !== 'null' ? val : null;
    }
    // If it's an object, try known ID keys
    const id = obj._assetId ?? obj.value ?? obj.id;
    if (id == null) return null;
    const val = String(id);
    return val && val !== 'undefined' && val !== 'null' ? val : null;
  }

  async onSave() {
    if (this.isSubmitting) return;

    const fulfillments: FulfillmentItem[] = [];
    const transferPromises: { row: NotAvailableAssetRow; index: number }[] = [];

    // Validate and collect available asset rows (inventory fulfillment)
    for (let i = 0; i < this.availableAssetRows.length; i++) {
      const row = this.availableAssetRows[i];
      if (!row.asset) continue;

      const assetId = this.extractId(row.asset);
      if (!assetId) {
        this.toasterService.error(`Available Asset Row ${i + 1}: Could not determine asset ID. Please re-select the asset.`);
        return;
      }

      const item: FulfillmentItem = {
        asset_request_item_id: String(this.itemId),
        fulfillment_type: 'inventory',
        quantity: 1,
        asset_id: assetId,
      };

      const userId = this.extractId(row.assignToPerson);
      if (userId) {
        item.assigned_to_user_id = userId;
      }

      const deptId = this.extractId(row.assignToDepartment);
      if (deptId) {
        item.assigned_to_department_id = deptId;
      }

      fulfillments.push(item);
    }

    // Collect not available asset rows
    for (let i = 0; i < this.notAvailableAssetRows.length; i++) {
      const row = this.notAvailableAssetRows[i];

      if (row.fulfillmentType === 'procurement') {
        if (row.quantity < 1) {
          this.toasterService.error(
            `Row ${i + 1}: Quantity must be at least 1`
          );
          return;
        }
        fulfillments.push({
          asset_request_item_id: String(this.itemId),
          fulfillment_type: 'procurement',
          quantity: row.quantity,
          ...(row.itemMaster?.id ? { item_master_id: String(row.itemMaster.id) } : {}),
        });
      } else if (row.fulfillmentType === 'transfer') {
        if (!row.asset) {
          this.toasterService.error(`Row ${i + 1}: Please select an asset for transfer`);
          return;
        }
        if (!row.transferTargetSubsidiary || !row.transferTargetLocation) {
          this.toasterService.error(
            `Row ${i + 1}: Target location is required for transfer`
          );
          return;
        }
        transferPromises.push({ row, index: i });
      }
    }

    if (fulfillments.length === 0 && transferPromises.length === 0) {
      this.toasterService.error('Please add at least one fulfillment entry');
      return;
    }

    // Validate: loan return date must be after transfer date
    for (const { row, index } of transferPromises) {
      const tfv = row.transferForm.value;
      if (tfv.transfer_type === 'loan' && tfv.loan_return_date && tfv.requested_date) {
        if (tfv.loan_return_date <= tfv.requested_date) {
          this.toasterService.error(`Row ${index + 1}: Loan return date must be after transfer date`);
          return;
        }
      }
    }

    // Validate: no duplicate assets across all rows
    const allAssetIds = new Set<string>();
    for (const f of fulfillments) {
      if (f.asset_id) {
        if (allAssetIds.has(f.asset_id)) {
          this.toasterService.error('Duplicate asset selected. Each asset can only be used once.');
          return;
        }
        allAssetIds.add(f.asset_id);
      }
    }
    for (const { row } of transferPromises) {
      const aid = this.extractId(row.asset);
      if (aid) {
        if (allAssetIds.has(aid)) {
          this.toasterService.error('Duplicate asset selected. Each asset can only be used once.');
          return;
        }
        allAssetIds.add(aid);
      }
    }

    // Build transfer fulfillment items with inline transfer details
    for (const { row } of transferPromises) {
      const assetId = this.extractId(row.asset);
      const sourceSubId = this.extractId(row.asset?.subsidiary_id) || this.extractId(this.requestData.subsidiary_id);
      const sourceLocId = this.extractId(row.asset?.location_id) || this.extractId(this.requestData.location_id);
      const destSubId = this.extractId(row.transferTargetSubsidiary);
      const destLocId = this.extractId(row.transferTargetLocation);

      if (!assetId || !sourceSubId || !sourceLocId || !destSubId || !destLocId) {
        this.toasterService.error('Transfer request is missing required fields. Please fill all transfer details.');
        return;
      }

      const tfv = row.transferForm.value;
      const transferDetails: any = {
        asset_id: assetId,
        source_subsidiary_id: sourceSubId,
        source_location_id: sourceLocId,
        destination_subsidiary_id: destSubId,
        destination_location_id: destLocId,
        transfer_type: tfv.transfer_type || 'permanent',
        transfer_mode: tfv.transfer_mode || 'internal',
        remarks: tfv.remarks || '',
      };
      if (tfv.destination_department) {
        transferDetails.destination_department_id = tfv.destination_department;
      }
      if (tfv.requested_date) {
        transferDetails.requested_date = this.formatDate(tfv.requested_date);
      }
      if (tfv.loan_return_date) {
        transferDetails.loan_return_date = this.formatDate(tfv.loan_return_date);
      }
      if (tfv.expected_delivery_date) {
        transferDetails.expected_delivery_date = this.formatDate(tfv.expected_delivery_date);
      }
      if (tfv.delivery_challan_no) {
        transferDetails.delivery_challan_no = tfv.delivery_challan_no;
      }

      fulfillments.push({
        asset_request_item_id: String(this.itemId),
        fulfillment_type: 'transfer',
        quantity: 1,
        transfer_details: transferDetails,
      });
    }

    this.isSubmitting = true;
    try {
      const res = await lastValueFrom(
        this.fulfillmentService.createFulfillment(this.requestId, {
          fulfillments,
        })
      );

      if (res?.data?.errors?.length > 0) {
        this.toasterService.error(
          `Some fulfillments failed: ${res.data.errors.join(', ')}`
        );
      } else {
        this.toasterService.success('Fulfillments created successfully');
      }

      this.mode = 'view';
      this.availableAssetRows = [];
      this.notAvailableAssetRows = [];
      await this.loadData();
      this.router.navigate([], {
        relativeTo: this.route,
        queryParams: { itemId: this.itemId, mode: 'view' },
        queryParamsHandling: 'replace',
      });
    } catch (error: any) {
      const message =
        error?.error?.message || 'Failed to create fulfillments';
      this.toasterService.error(message);
    } finally {
      this.isSubmitting = false;
    }
  }

  onCancel() {
    this.router.navigate(['/assets/asset-request', this.requestId], {
      queryParams: { mode: 'view' },
    });
  }

  // ============================================
  // Helpers
  // ============================================

  getStatusSeverity(
    status: string
  ): 'success' | 'info' | 'warn' | 'danger' | 'secondary' | 'contrast' {
    switch (status) {
      case 'completed':
        return 'success';
      case 'fulfilled':
        return 'success';
      case 'pending':
        return 'warn';
      case 'cancelled':
      case 'rejected':
        return 'danger';
      case 'approved':
        return 'success';
      case 'in_transit':
      case 'asset_in_transit':
      case 'po_created':
      case 'grn_created':
      case 'far_created':
        return 'info';
      default:
        return 'info';
    }
  }

  private formatStatus(status: string): string {
    if (!status) return '-';
    return status
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  }

  getStatusLabel(fulfillment: any): string {
    switch (fulfillment.fulfillment_type) {
      case 'inventory':
        return fulfillment.status === 'completed' ? 'Fulfilled' : this.formatStatus(fulfillment.status);
      case 'procurement':
        return fulfillment.status === 'completed' ? 'Fulfilled' : this.formatStatus(fulfillment.status);
      case 'transfer':
        return fulfillment.status === 'completed' ? 'Fulfilled' : 
          fulfillment.status === 'pending'
          ? 'Transfer Request Created'
          : this.formatStatus(fulfillment.status);
      default:
        return this.formatStatus(fulfillment.status);
    }
  }

  formatFulfillmentType(type: string): string {
    switch (type) {
      case 'inventory':
        return 'Assign Existing';
      case 'procurement':
        return 'Purchase New';
      case 'transfer':
        return 'Transfer';
      default:
        return type || '-';
    }
  }

  private formatDate(date: Date | string): string {
    if (typeof date === 'string') {
      date = new Date(date);
    }
    const y = date.getFullYear();
    const m = String(date.getMonth() + 1).padStart(2, '0');
    const d = String(date.getDate()).padStart(2, '0');
    return `${y}-${m}-${d}`;
  }

  getRemainingQuantity(): number {
    const total = this.itemData?.quantity || 0;
    const fulfilled = this.itemData?.fulfilled_quantity || 0;
    return Math.max(0, total - fulfilled);
  }

  // GRN Assets Modal
  async openGrnAssetsModal(fulfillment: any) {
    this.grnModalFulfillment = fulfillment;
    this.grnAssets = [];
    this.selectedGrnAssets = [];
    this.showGrnAssetsModal = true;

    try {
      const res: any = await lastValueFrom(
        this.fulfillmentService.getAssetsByGrnId(String(fulfillment.grn_id))
      );
      if (res?.data?.assets) {
        // Get IDs of already-selected assets for this fulfillment
        const fulfillmentId = String(fulfillment.id);
        const alreadySelectedIds = new Set(
          (this.fulfillmentAssignments.get(fulfillmentId) || []).map((a: any) => String(a.asset_id))
        );

        // Filter out assets that are assigned or already selected
        this.grnAssets = res.data.assets.filter(
          (asset: any) => !asset.assigned_to_user_id && !asset.assigned_to_department_id && !alreadySelectedIds.has(String(asset.id))
        );
      }
      this.cdr.detectChanges();
    } catch (err) {
      this.toasterService.error('Failed to fetch GRN assets');
    }
  }

  onGrnAssetsSave() {
    if (this.selectedGrnAssets.length === 0) {
      this.toasterService.error('Please select at least one asset');
      return;
    }

    // Store selected assets with assignment fields for this fulfillment
    const fulfillmentId = String(this.grnModalFulfillment.id);
    const newRows = this.selectedGrnAssets.map((asset: any) => ({
      asset_id: String(asset.id),
      asset_name: asset.asset_name,
      asset_code: asset.asset_code || '',
      serial_number: asset.serial_number,
      model_no: asset.model_no || '',
      assignedUser: null as any,
      assignedDepartment: null as any,
      userSuggestions: [] as any[],
      departmentSuggestions: [...this.departmentOptions],
    }));

    // Append to existing assignments
    const existing = this.fulfillmentAssignments.get(fulfillmentId) || [];
    this.fulfillmentAssignments.set(fulfillmentId, [...existing, ...newRows]);
    this.expandedFulfillmentId = fulfillmentId;
    this.showGrnAssetsModal = false;
    this.cdr.detectChanges();
  }

  async toggleFulfillmentExpand(fulfillmentId: string) {
    if (this.expandedFulfillmentId === fulfillmentId) {
      this.expandedFulfillmentId = null;
    } else {
      this.expandedFulfillmentId = fulfillmentId;

      const f = this.fulfillments.find(
        (ful) => String(ful.id) === fulfillmentId
      );

      if (
        f &&
        f.fulfillment_type === 'inventory' &&
        f.asset_id &&
        !this.fulfillmentAssetDetails.has(fulfillmentId)
      ) {
        try {
          this.isLoading = true;
          const res = await lastValueFrom(
            this.assetService.getAsset(f.asset_id)
          );
          if (res?.data) {
            this.fulfillmentAssetDetails.set(fulfillmentId, res.data);
          }
        } catch (error) {
          console.error('Error fetching asset details:', error);
        } finally {
          this.isLoading = false;
          this.cdr.detectChanges();
        }
      }
    }
  }

  viewAssetHistory(assetId: string) {
    if (assetId) {
      this.selectedAssetIdForHistory = assetId;
      this.historyModalVisible = true;
    }
  }

  isFulfillmentExpanded(fulfillmentId: string): boolean {
    return this.expandedFulfillmentId === fulfillmentId;
  }

  hasAssignments(fulfillmentId: string): boolean {
    return this.fulfillmentAssignments.has(fulfillmentId) &&
           (this.fulfillmentAssignments.get(fulfillmentId)?.length ?? 0) > 0;
  }

  hasExistingAssignments(f: any): boolean {
    if (f.fulfillment_type === 'inventory') {
      return !!f.asset_id;
    }
    const assignments = this.fulfillmentAssignments.get(String(f.id)) || [];
    return assignments.some((a: any) => a.isExisting);
  }

  getAssignments(fulfillmentId: string): any[] {
    return this.fulfillmentAssignments.get(fulfillmentId) || [];
  }

  getFulfillmentAssetDetails(fulfillmentId: string): any {
    return this.fulfillmentAssetDetails.get(fulfillmentId);
  }


  async searchAssignmentUsers(event: any, assignmentRow: any) {
    const query = event.query || '';
    try {
      const res = await lastValueFrom(
        this.userService.getAllUsers({
          limit: 50,
          offset: 0,
          is_active: true,
          ...(query ? { name: query } : {}),
        })
      );
      assignmentRow.userSuggestions = (res.users || []).map((u: any) => ({
        label: u.username,
        value: u.id,
      }));
    } catch {
      assignmentRow.userSuggestions = [];
    }
  }

  searchAssignmentDepartments(event: any, assignmentRow: any) {
    const query = (event.query || '').toLowerCase();
    assignmentRow.departmentSuggestions = this.departmentOptions.filter(
      (d: any) => d.label.toLowerCase().includes(query)
    );
  }

  async saveAllAssignments() {
    // Collect all assignments across all fulfillments
    const fulfillmentIds = Array.from(this.fulfillmentAssignments.keys());
    if (fulfillmentIds.length === 0) {
      this.toasterService.error('No assets to assign');
      return;
    }

    let hasNewAssignments = false;

    // Validate all NEW rows have at least user or department
    for (const fId of fulfillmentIds) {
      const assignments = this.getAssignments(fId);
      for (const row of assignments) {
        if (!row.isExisting) {
          hasNewAssignments = true;
          if (!row.assignedUser && !row.assignedDepartment) {
            this.toasterService.error('Please assign a user or department for each new asset');
            return;
          }
        }
      }
    }

    if (!hasNewAssignments) {
      this.toasterService.success('No new assignments to save');
      // just collapse
      this.expandedFulfillmentId = null;
      return;
    }

    try {
      this.isLoading = true;
      let totalAssigned = 0;

      for (const fId of fulfillmentIds) {
        const assignments = this.getAssignments(fId);
        // Only get newly added assignments
        const newAssignments = assignments.filter((a: any) => !a.isExisting);
        if (newAssignments.length === 0) continue;

        const payload = newAssignments.map((row: any) => ({
          asset_id: row.asset_id,
          assigned_to_user_id: row.assignedUser || null,
          assigned_to_department_id: row.assignedDepartment || null,
        }));
        const res = await lastValueFrom(
          this.fulfillmentService.bulkAssignAssets(fId, payload)
        );
        totalAssigned += res?.data?.assigned_count || newAssignments.length;
      }

      this.toasterService.success(`${totalAssigned} asset(s) assigned successfully`);
      this.expandedFulfillmentId = null;
      
      // Reload to fetch the exact saved items
      await this.loadFulfillments();
      this.cdr.detectChanges();
    } catch (err: any) {
      console.error('Error saving assignments:', err);
      const errorMsg = err?.error?.message || err?.message || 'Failed to save assignments';
      this.toasterService.error(errorMsg);
    } finally {
      this.isLoading = false;
    }
  }

  hasNewAssignments(): boolean {
    for (const rows of this.fulfillmentAssignments.values()) {
      if (rows.some((r: any) => !r.isExisting)) {
        return true;
      }
    }
    return false;
  }
}
